/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as AuditDAO from 'audit/AuditDAO';
import {
  formatDataModsMoveDisallowedErrorMessage,
  isProjectLevelDataModsEnabled,
} from 'audit/DataModifications';
import BenchlingConfig from 'BenchlingConfig';
import {FolderItem} from 'constants/FileBrowserConstants';
import {MinimalFolder, Type} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isRegistrySupportedType} from 'constants/RegistryConstants';
import {FullSource, SourceSummary} from 'constants/SourceConstants';
import CreateSourceModal from 'create-components/CreateSourceModal';
import {getOwnerChoices} from 'create-components/OwnableUtil';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import {capitalize, typeToName, typeToPluralized} from 'util/NameUtil';
import {GenericEvent} from 'util/Types';

import CreateFolderForm from './CreateFolderForm';
import FileBrowserUtil from './FileBrowserUtil';
import FolderDAO from './FolderDAO';
import FolderSelectTree from './FolderSelectTree';
import LabeledFolder from './LabeledFolder';
import RegistrySelectTree from './RegistrySelectTree';

const FOLDER_SELECTOR_MODAL_KEY = 'FolderSelectorModal';

export const FOLDER_TYPES = {
  INVENTORY: 'inventory_folder',
  NOTEBOOK: 'notebook_folder',
};

// Renders a modal for finalizing folder selection from a SelectTree

export interface FolderSelectorModalProps {
  addFolder(folder: MinimalFolder): void;
  folders: Array<MinimalFolder>;
  isExistingItem: boolean;
  onChange(e: GenericEvent): void | Promise<void>; // TODO(BNCH-10194): should be onChange(folderId: string | null)
  onRequestClose(): void;
  submitText: string;
  submitButtonStyle?: ButtonStyle;
  allowNone: boolean;
  allowCreateFolder: boolean;
  showNotebook: boolean;
  showInventory: boolean;
  showRegistries: boolean;
  requireSubfolderCreatableAuth: boolean;
  title: string;
  itemsToMove?: Array<Partial<FolderItem> & {id: string}>;
  initialFolderId: string | null;
  zIndex?: 1040 | 1060;
}

// This component is often rendered indirectly through open, openForSelect, openForCopy, or openForMove.
// These methods take a subset of the properties used when rendering FolderSelectorModal directly.
export interface FolderSelectorModalOptions extends Partial<FolderSelectorModalProps> {
  initialSelectedFolderId?: string;
}

interface State {
  folderId: string;
  showCreateForm: boolean;
  isRegistry: boolean;
  isSaving: boolean;
  isMoveAllowedByDataMods: boolean;
  moveDisallowedReason: string;
}

export default class FolderSelectorModal extends React.Component<FolderSelectorModalProps, State> {
  static defaultProps: Partial<FolderSelectorModalProps> = {
    allowNone: false,
    allowCreateFolder: true,
    isExistingItem: false,
    requireSubfolderCreatableAuth: false,
    showInventory: false,
    showNotebook: false,
    showRegistries: false,
    submitText: 'Select',
  };

  constructor(props: FolderSelectorModalProps) {
    super(props);

    const folder = _.findWhere(props.folders, {api_identifier: props.initialFolderId});

    this.state = {
      folderId: props.initialFolderId || '',
      showCreateForm: false, // Boolean indicating if the create form is shown
      isRegistry: Boolean(folder && RegistryUtil.isRegistryFolder(folder)),
      isSaving: false,
      isMoveAllowedByDataMods: true,
      moveDisallowedReason: '',
    };
  }

  _isMounted = false;

  static fetchFolders(
    {level}: {level?: IntentLevel},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (folders: Array<MinimalFolder>) => any
  ): void {
    if (!level) {
      level = 'append';
    }
    LoadingScreen.show();
    FolderDAO.fetchAll({level})
      .always(() => {
        LoadingScreen.hide();
      })
      .fail((res) => {
        notifications.notifyError(res);
      })
      .done((folders: Array<MinimalFolder>) => {
        callback(folders);
      });
  }

  static withFolderAdded(folders: Array<MinimalFolder>, folder: MinimalFolder): Array<MinimalFolder> {
    const index = _.sortedIndex(folders, folder, 'name');
    return reactUpdate(folders, {
      $splice: [[index, 0, folder]],
    });
  }

  static makeFolderSectionFlags(
    showInventory?: boolean,
    showNotebook?: boolean
  ): {showInventory: boolean; showNotebook: boolean} {
    // If either flag is true, we are showing folders and should show both sections on NIM tenants but if
    // both are false they should both stay false because we only want to show ALL, root project folders
    const flagValue = showInventory || showNotebook || false;
    return {showInventory: flagValue, showNotebook: flagValue};
  }

  static open(props: FolderSelectorModalOptions): void {
    const {showInventory, showNotebook} = FolderSelectorModal.makeFolderSectionFlags(
      props.showInventory,
      props.showNotebook
    );

    LegacyModalContainer.open(
      FOLDER_SELECTOR_MODAL_KEY,
      <FolderSelectorModal
        // eslint-disable-next-line no-restricted-syntax
        {..._.extend(
          {
            onRequestClose: () => {
              FolderSelectorModal.close();
            },
          },
          {...props, showInventory, showNotebook}
        )}
      />
    );
  }

  static openForSelect(
    level: IntentLevel,
    modalOptions: FolderSelectorModalOptions,
    callback: (e: GenericEvent) => void
  ): void {
    function openModal(folders: Array<MinimalFolder>): void {
      // eslint-disable-next-line no-restricted-syntax
      const options = _.extend({}, modalOptions, {
        addFolder(folder: MinimalFolder): void {
          openModal(FolderSelectorModal.withFolderAdded(folders, folder));
        },
        folders,
        onChange: callback,
      });
      FolderSelectorModal.open(options);
    }

    FolderSelectorModal.fetchFolders({level}, openModal);
  }

  static close(): void {
    LegacyModalContainer.close(FOLDER_SELECTOR_MODAL_KEY);
  }

  static openForMove(
    options: FolderSelectorModalOptions,
    callback: (folderId: string, registryId: string) => void
  ): void {
    this._openForMoveCopy(true, options, callback);
  }

  static openForCopy(
    options: FolderSelectorModalOptions,
    callback: (folderId: string, registryId: string) => void
  ): void {
    this._openForMoveCopy(false, options, callback);
  }

  /**
   * Opens the folder picker as a first-class "Unregister" action. Unregistering a registered item
   * moves it out of the registry-as-folder into a real folder, so it reuses the move flow, but the
   * modal leads with the "Unregister" framing rather than exposing the underlying move.
   */
  static openForUnregister(
    options: FolderSelectorModalOptions,
    callback: (folderId: string, registryId: string) => void
  ): void {
    this._openForMoveCopy(true, options, callback, {
      title: 'Unregister',
      submitText: 'Unregister',
      // Unregistering is destructive, so the confirm button matches the red styling of Archive.
      submitButtonStyle: ButtonStyle.NEGATIVE,
    });
  }

  static _openForMoveCopy(
    isMove: boolean,
    options: FolderSelectorModalOptions,
    callback: (folderId: string | null, registryId: string | null) => void,
    overrides?: {title?: string; submitText?: string; submitButtonStyle?: ButtonStyle}
  ): void {
    const operationText = isMove ? 'Move' : 'Copy';

    function openModal(folders: Array<MinimalFolder>): void {
      FolderSelectorModal.open({
        addFolder(folder: MinimalFolder): void {
          openModal(FolderSelectorModal.withFolderAdded(folders, folder));
        },
        allowNone: options.allowNone,
        folders,
        isExistingItem: true,
        onChange(e: GenericEvent): void {
          const {folderId, registryId} = RegistryUtil.parseFolderValue(e.target.value);
          FolderSelectorModal.close();
          callback(folderId, registryId);
        },
        itemsToMove: options.itemsToMove,
        initialFolderId: options.initialSelectedFolderId,
        showInventory: options.showInventory || false,
        showNotebook: options.showNotebook || false,
        showRegistries: options.showRegistries || false,
        submitText: overrides?.submitText ?? operationText,
        submitButtonStyle: overrides?.submitButtonStyle,
        title: overrides?.title ?? `${operationText} To...`,
      });
    }

    FolderSelectorModal.fetchFolders({level: 'append'}, openModal);
  }

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  resetFolder = (): void => {
    this.setState({folderId: this.props.initialFolderId || ''});
  };

  submitHandler = async (): Promise<void> => {
    if (!this._getValidInfo().isValid) {
      return;
    }

    if (
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS &&
      !(await this.isMoveAllowedByDataMods())
    ) {
      return;
    }

    this.setState({isSaving: true});
    try {
      // onChange expects an event... we pass a stripped-down event.
      await this.props.onChange({target: {value: this.state.folderId}});
    } finally {
      this.setState({isSaving: false});
    }
    this.props.onRequestClose();
  };

  isMoveAllowedByDataMods = async (): Promise<boolean> => {
    if (
      !BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS ||
      // only care about checking folders of different data mods settings if project level data mods is enabled
      !isProjectLevelDataModsEnabled() ||
      !this.props.isExistingItem // nothing to move, as item has not been created yet
    ) {
      this.setMoveIsAllowedByDataMods();
      return true;
    }

    const apiIds = [this.props.initialFolderId];
    const resp = await AuditDAO.checkFolderMoveAllowedByDataMods(apiIds, this.state.folderId);
    if (!resp.move_allowed) {
      this.setState({
        isMoveAllowedByDataMods: false,
        moveDisallowedReason: formatDataModsMoveDisallowedErrorMessage(resp.disallowed_api_ids),
      });
      return false;
    } else {
      this.setMoveIsAllowedByDataMods();
      return true;
    }
  };

  setMoveIsAllowedByDataMods = (): void => {
    this.setState({isMoveAllowedByDataMods: true, moveDisallowedReason: ''});
  };

  toggleCreate = (showCreateForm: boolean): void => {
    this.setState({showCreateForm});
  };

  createFolder = (name: string): JQueryPromise<MinimalFolder> => {
    const type = Type.ARBITRARY;
    return FolderDAO.create({
      parent: this.state.folderId,
      name,
      type,
    })
      .done((folder) => {
        FolderDAO.registerCreatedFolder(folder!);
        this.props.addFolder(folder!);
        if (this._isMounted) {
          this.setState({showCreateForm: false, folderId: folder!.api_identifier});
        }
      })
      .fail((res) => {
        notifications.notifyError(res);
      });
  };

  createProjectHandler = (): void => {
    if (getOwnerChoices().length === 0) {
      notifications.error('Please contact your administrator to create a project');
      return;
    }
    legacyRunModal(CreateSourceModal, {
      navigateOnSuccess: false,
      onCreateComplete: (source?: SourceSummary & Pick<FullSource, 'permissions'>) => {
        if (!this._isMounted || source == null) {
          return;
        }
        // Construct the folder from the source.
        const folder: MinimalFolder = {
          ...source.rootFolder,
          id: source.rootFolder.api_identifier,
          parent: null,
          permissions: source.permissions,
          source,
        };
        this.props.addFolder(folder);
        this.setState({folderId: folder.api_identifier, isRegistry: false});
      },
    });
  };

  _getValidInfo = (): {isValid: boolean; message: string} => {
    if (this.state.isRegistry && this.props.itemsToMove) {
      const fileTypes = this.props.itemsToMove.map((item) => APIUtil.apiIdToType(item.id));
      if (fileTypes.length > 0) {
        // eslint-disable-next-line no-restricted-syntax
        const invalidType = _.find(fileTypes, (fileType) => !isRegistrySupportedType(fileType));
        if (invalidType != null) {
          const typeName = capitalize(typeToPluralized(invalidType));
          return {
            isValid: false,
            message: `${typeName} cannot be added to a registry`,
          };
        }
        // eslint-disable-next-line no-restricted-syntax
        if (!_.every(this.props.itemsToMove, (item) => 'tagSchema' in item && item.tagSchema != null)) {
          return {
            isValid: false,
            message:
              'All files being moved into a registry must have a schema set. This can be set in ' +
              'the metadata section of the file.',
          };
        }
        const {registryId} = RegistryUtil.parseFolderValue(this.state.folderId);
        if (
          // eslint-disable-next-line no-restricted-syntax
          !_.every(
            this.props.itemsToMove,
            (item) => 'tagSchema' in item && registryId === item.tagSchema?.registryId
          )
        ) {
          return {
            isValid: false,
            message:
              'Some files being moved have schemas that do not belong to the registry you have selected',
          };
        }
      }
    }

    const folder = _.findWhere(this.props.folders, {api_identifier: this.state.folderId});
    if (
      this.props.requireSubfolderCreatableAuth &&
      folder != null &&
      folder.permissions &&
      !folder.permissions.subfolderCreatable
    ) {
      return {
        isValid: false,
        message: 'You must select a folder you have permission to create new folders in.',
      };
    }

    return {
      isValid: this.props.allowNone || this.state.folderId !== '',
      message: 'You must select a folder',
    };
  };

  _getNames = (): {name: string; pluralized: string} => {
    let type;
    if (this.props.showInventory && this.props.showNotebook) {
      type = ModelTypeConstants.FOLDER;
    } else if (!this.props.showInventory && !this.props.showNotebook) {
      type = ModelTypeConstants.PROJECT;
    } else if (this.props.showInventory && !this.props.showNotebook) {
      type = FOLDER_TYPES.INVENTORY;
    } else if (!this.props.showInventory && this.props.showNotebook) {
      type = FOLDER_TYPES.NOTEBOOK;
    } else {
      throw new Error(`Invalid props showInventory: ${this.props.showInventory}, \
showNotebook:  ${this.props.showInventory}`);
    }
    const name = typeToName(type);
    const pluralized = typeToPluralized(type);
    return {name, pluralized};
  };

  _handleSelectProjectFolder = (folderId: string): void => {
    this.setMoveIsAllowedByDataMods();
    const folder = _.findWhere(this.props.folders, {api_identifier: folderId});
    const canCreateSubfolder = folder && folder.permissions && folder.permissions.subfolderCreatable;
    if (canCreateSubfolder) {
      this.setState({folderId, isRegistry: false});
    } else {
      // Ensure create form is toggled off after selecting a folder you can't create subfolders in
      this.setState({
        folderId,
        isRegistry: false,
        showCreateForm: false,
      });
    }
  };

  _computeFolderPath = (folder: MinimalFolder): string => {
    const path = [folder.name];
    while (folder.parent != null) {
      folder = folder.parent;
      path.unshift(folder.name);
    }
    path.unshift(folder.owner_handle);
    return path.join(' / ');
  };

  renderCreateForm = (): JSX.Element | null => {
    if (!this.props.allowCreateFolder || this.state.folderId === '' || this.state.isRegistry) {
      return null;
    }
    // Cannot create folders in project selector modal
    if (!this.props.showNotebook && !this.props.showInventory) {
      return null;
    }
    const folder = _.findWhere(this.props.folders, {api_identifier: this.state.folderId});
    if (folder == null) {
      return null;
    }

    // Compute breadcrumb
    const path = this._computeFolderPath(folder);

    return (
      <div key="create-form">
        <div className="u-small-spacing-bottom">
          {folder.permissions && folder.permissions.subfolderCreatable ? (
            <span className="u-overflow-wrap-break-word">
              <a
                onClick={this.toggleCreate.bind(null, !this.state.showCreateForm)}
                className="u-tiny-spacing-right u-valign-middle"
                {...getTestProps({component: 'FolderSelectorModal', element: 'link', key: 'createForm'})}
                data-amp-safe={false}
              >
                Create new folder
              </a>
              <span className="u-small-text u-no-mouse-target faded u-valign-middle">{`(${path})`}</span>
            </span>
          ) : (
            <span className="u-small-text u-no-mouse-target faded u-valign-middle">
              You do not have permission to create a new folder in the selected folder.
            </span>
          )}
        </div>
        {this.state.showCreateForm && folder.permissions && folder.permissions.subfolderCreatable && (
          <CreateFolderForm createFolder={this.createFolder} />
        )}
      </div>
    );
  };

  override render(): JSX.Element {
    const {name, pluralized} = this._getNames();
    return (
      <Modal
        title={this.props.title != null ? this.props.title : `Select a ${name}`}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.submitHandler}
        wrapInForm={false}
        buttons={[
          {
            text: this.props.submitText,
            style: this.props.submitButtonStyle,
            disabled:
              this.props.folders.length === 0 ||
              !this._getValidInfo().isValid ||
              this.state.isSaving ||
              !this.state.isMoveAllowedByDataMods,
            submit: true,
            dataTestAttrs: {component: 'FolderSelectorModal', element: 'submit'},
          },
        ]}
        zIndex={this.props.zIndex}
        dataTestAttrs={{component: 'FolderSelectorModal', element: 'root'}}
      >
        {(() => {
          if (this.props.initialFolderId != null && this.props.isExistingItem) {
            const folder = _.findWhere(this.props.folders, {api_identifier: this.props.initialFolderId});
            if (folder != null) {
              return (
                <div className="well well-sm">
                  {'Item is currently in: '}
                  <LabeledFolder
                    className="u-tiny-spacing-left u-pointer"
                    name={folder.name}
                    onClick={this.resetFolder}
                  />
                </div>
              );
            }
          }
          return null;
        })()}
        {(() => {
          if (this.props.folders.length === 0) {
            return (
              <div>
                {`No ${pluralized} were found. To continue, create a `}
                <a onClick={this.createProjectHandler}>new project</a>
                {' first.'}
              </div>
            );
          } else {
            // eslint-disable-next-line prefer-const
            let [registries, folders] = Array.from(
              _.partition(this.props.folders, (folder) =>
                Boolean(folder.source && folder.source.sourceType === 'registry')
              )
            );
            const predicate = FileBrowserUtil.propsToFolderPredicate(this.props);
            folders = folders.filter((folder) => predicate(folder));
            return [
              this.props.showRegistries && registries.length > 0 && (
                <div key="registry-select-tree">
                  <h5>Registries</h5>
                  <RegistrySelectTree
                    registries={registries}
                    folderIds={_.compact([this.state.folderId])}
                    onChangeSelectedFolders={(folderIds: Array<string>) => {
                      assert(folderIds.length <= 1, 'Expected 1 or fewer registries');
                      this.setState({folderId: folderIds[0], isRegistry: true});
                    }}
                    submitHandler={this.submitHandler}
                  />
                </div>
              ),
              <div key="folder-select-tree">
                <h5>Projects</h5>
                <FolderSelectTree
                  allowNone={this.props.allowNone}
                  folders={folders}
                  folderIds={_.compact([this.state.folderId])}
                  onChangeSelectedFolders={(folderIds: Array<string>) => {
                    assert(folderIds.length <= 1, 'Expected 1 or fewer folders');
                    this._handleSelectProjectFolder(folderIds[0]);
                  }}
                  multiple={false}
                  submitHandler={this.submitHandler}
                />
              </div>,
              !this._getValidInfo().isValid ? (
                <p
                  className="error-msg"
                  key="error"
                  {...getTestProps({component: 'FolderSelectorModal', element: 'errorMessage'})}
                >
                  {this._getValidInfo().message}
                </p>
              ) : (
                this.renderCreateForm()
              ),
              !this.state.isMoveAllowedByDataMods && (
                <InlineBanner
                  bannerType={InlineBannerType.ERROR}
                  text={this.state.moveDisallowedReason}
                  key="error-banner"
                />
              ),
            ];
          }
        })()}
      </Modal>
    );
  }
}
